<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.auth-wrapper {
  .login-bg {
    background-size: cover;
    background-position: center;
  }
}
</style>

<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Left panel -->
      <b-col
        lg="8"
        :style="{'background-image': 'url(' + bgImage + ')'}"
        class="d-none d-lg-flex align-items-center p-5 login-bg"
      />
      <!-- /Left panel -->

      <!-- Form -->
      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            Set a new password
          </b-card-title>
          <b-card-text class="mb-2">
            For security, you must change your password before continuing.
          </b-card-text>

          <validation-observer ref="observer" v-slot="{ handleSubmit }">
            <b-form @submit.stop.prevent="handleSubmit(submit)">

              <b-form-group label="Current Password" label-for="current-password">
                <validation-provider v-slot="validationContext" name="Current Password" :rules="{ required: true }">
                  <b-input-group>
                    <b-form-input
                      id="current-password"
                      v-model="form.current_password"
                      :state="getValidationState(validationContext)"
                      :type="showCurrent ? 'text' : 'password'"
                      placeholder="Enter current password"
                    />
                    <b-input-group-append>
                      <b-button @click="showCurrent = !showCurrent">
                        <i class="fas" :class="showCurrent ? 'fa-eye' : 'fa-eye-slash'" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ validationContext.errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="New Password" label-for="new-password">
                <validation-provider v-slot="validationContext" name="New Password" :rules="{ required: true, min: 8 }">
                  <b-input-group>
                    <b-form-input
                      id="new-password"
                      v-model="form.new_password"
                      :state="getValidationState(validationContext)"
                      :type="showNew ? 'text' : 'password'"
                      placeholder="Enter new password"
                    />
                    <b-input-group-append>
                      <b-button @click="showNew = !showNew">
                        <i class="fas" :class="showNew ? 'fa-eye' : 'fa-eye-slash'" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ validationContext.errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Confirm New Password" label-for="confirm-password">
                <validation-provider v-slot="validationContext" name="Confirm New Password" :rules="{ required: true, is: form.new_password }">
                  <b-input-group>
                    <b-form-input
                      id="confirm-password"
                      v-model="form.confirm_password"
                      :state="getValidationState(validationContext)"
                      :type="showConfirm ? 'text' : 'password'"
                      placeholder="Re-enter new password"
                    />
                    <b-input-group-append>
                      <b-button @click="showConfirm = !showConfirm">
                        <i class="fas" :class="showConfirm ? 'fa-eye' : 'fa-eye-slash'" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ validationContext.errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-button type="submit" variant="primary" block :disabled="loading">
                {{ loading ? 'Saving...' : 'Set Password' }}
              </b-button>

              <p class="text-center mt-2">
                <b-link @click="signOut">Sign out</b-link>
              </p>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
      <!-- /Form -->

    </b-row>
  </div>
</template>

<script>
import PrisonUsersService from '@/services/PrisonUsersService';
import { PASSWORD_ERROR_CODES } from '@/constants/errorCodes';

export default {
  name: 'ForceChangePassword',
  data() {
    return {
      loading: false,
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      bgImage: require('@/assets/images/pages/login-bg.jpg'),
      form: {
        current_password: '',
        new_password: '',
        confirm_password: '',
      },
    };
  },
  methods: {
    submit() {
      this.loading = true;
      PrisonUsersService.changePassword(this.form.current_password, this.form.new_password)
        .then(() => PrisonUsersService.getUser())
        .then(res => {
          this.$store.dispatch('auth/setLocalUser', res.data);
          this.$toast.success('Password updated successfully', {
            toastClassName: ['toast-std', 'success-toast'],
          });
          this.$router.push({ name: 'dashboard' });
        })
        .catch(err => {
          this.$toast.error(this.getErrorMessage(err), {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    signOut() {
      this.$store.dispatch('auth/clearLocalUser');
      this.$router.push({ name: 'login' });
    },
    getErrorMessage(err) {
      const error = err.response && err.response.data && err.response.data.error;
      if (error === PASSWORD_ERROR_CODES.OLD_NEW_PASSWORD_SAME) return 'Your new password cannot be the same as your current password';
      if (error === PASSWORD_ERROR_CODES.INVALID_PASSWORD) return 'Your current password is incorrect';
      if (error === PASSWORD_ERROR_CODES.PASSWORD_TOO_SHORT) return 'Please enter a password of at least 8 characters';
      if (error === PASSWORD_ERROR_CODES.PASSWORD_TOO_LONG) return 'Please enter a password shorter than 128 characters';
      if (error === PASSWORD_ERROR_CODES.PASSWORD_INSECURE) return 'Please enter a more complex and secure password';
      return 'Could not update password, please try again';
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
  },
};
</script>
